import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Switch, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactComponent as PriceCheckLogoIcon } from '../../img/pricecheck-logo-icon.svg';
import { useTranslation } from 'react-i18next';

const PriceCheckSwitch = styled((props) => (
	<Switch disableRipple focusVisibleClassName="PriceCheckSwitch-focusVisible" {...props} />
))(({ theme }) => ({
	width: 36,
	height: 22,
	padding: 0,
	display: 'flex',
	alignItems: 'center',
	'& .MuiSwitch-switchBase': {
		padding: 0,
		margin: 2,
		transitionDuration: '150ms',
		'&.Mui-checked': {
			transform: 'translateX(14px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				backgroundColor: '#008F4B',
				opacity: 1,
				border: 0,
			},
		},
		'&.Mui-disabled + .MuiSwitch-track': {
			opacity: 0.3,
		},
	},
	'& .MuiSwitch-thumb': {
		width: 18,
		height: 18,
		borderRadius: 999,
		backgroundColor: '#FFFFFF',
		boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.15), 0px 3px 1px rgba(0, 0, 0, 0.06)',
	},
	'& .MuiSwitch-track': {
		borderRadius: 11,
		backgroundColor: '#C9EBD9',
		opacity: 1,
		boxSizing: 'border-box',
	},
}));

/**
 * Shared PriceCheck+ toggle card used on:
 * - Reservation page (before booking)
 * - Bookings drawer (after booking)
 */
const PriceCheckToggle = ({
	className,
	enabled,
	onChange,
	disabled,
	showInfoLink = true,
}) => {
	const { t } = useTranslation();
	const [isInfoOpen, setIsInfoOpen] = useState(false);

	const handleToggle = (event) => {
		if (typeof onChange === 'function') {
			onChange(event.target.checked);
		}
	};

	const title = t('PriceCheck.Title', 'PriceCheck+');
	const cardIntro = t('PriceCheck.CardIntro', "Arbitrip’s PriceCheck+");
	const cardBody = t(
		'PriceCheck.CardBody',
		' keeps an eye on this reservation and automatically rebooks it for you if it finds a better price.',
	);
	const dialogIntro = t('PriceCheck.DialogIntro', "Arbitrip’s PriceCheck+");
	const dialogBody = t(
		'PriceCheck.DialogBody',
		' continuously monitors your reservation for any price drops. If a lower price becomes available, it automatically rebooks your room at the better price, ensuring you always get the best deal without having to track prices or take any action yourself.',
	);
	const moreInformation = t('PriceCheck.MoreInformation', 'More information');
	const gotIt = t('PriceCheck.GotIt', 'Got it');

	return (
		<>
			<div className={classNames('pricecheck-card', className)}>
				<div className="pricecheck-card__header">
					<div className="pricecheck-card__brand">
						<div className="pricecheck-card__icon">
							<PriceCheckLogoIcon aria-hidden="true" />
						</div>
						<div className="pricecheck-card__logo">{title}</div>
						<div className="pricecheck-card__badge">Beta</div>
					</div>
					<PriceCheckSwitch
						checked={!!enabled}
						onChange={handleToggle}
						disabled={disabled}
						inputProps={{ 'aria-label': title }}
					/>
				</div>
				<div className="pricecheck-card__body">
					<div className="pricecheck-card__description">
						<span className="pricecheck-card__description-intro">{cardIntro}</span>
						{cardBody}
						{showInfoLink && (
							<>
								{' '}
								<button
									type="button"
									className="pricecheck-card__more-info"
									onClick={() => setIsInfoOpen(true)}
								>
									{moreInformation}
								</button>
							</>
						)}
					</div>
				</div>
			</div>

			<Dialog
				open={isInfoOpen}
				onClose={() => setIsInfoOpen(false)}
				maxWidth="sm"
				fullWidth
				className="pricecheck-dialog"
			>
				<DialogTitle disableTypography className="pricecheck-dialog__title">
					<div className="pricecheck-dialog__logo">
						<PriceCheckLogoIcon aria-hidden="true" />
						<span className="pricecheck-dialog__logo-text">{title}</span>
					</div>
				</DialogTitle>
				<DialogContent>
					<div className="pricecheck-dialog__content">
						<span className="pricecheck-dialog__content-intro">{dialogIntro}</span>
						{dialogBody}
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsInfoOpen(false)} autoFocus>
						{gotIt}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

PriceCheckToggle.propTypes = {
	className: PropTypes.string,
	enabled: PropTypes.bool.isRequired,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	showInfoLink: PropTypes.bool,
};

export default PriceCheckToggle;

